import React, { useRef, useState, useEffect } from "react";
import styles from "./UserServicesInfoTab.module.css";
import i18n from "../../Services/i18n";
import ImagePreview from "../ImagePreview/ImagePreview";
import DateTimeService from "../../Services/datetimeService";
import FileUploaderModal from "../FileUploaderModal/FileUploaderModal";
import UserServiceService from "../../Services/userServiceService";
import { useUserServices } from "../../Context/UserServiceContext";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import RichEditor from "../RichEditor/RichEditor";
import RichEditorHelper from "../../Tools/RichEditorHelper";
import { addToast } from "../../Services/toastService";
import FileService from "../../Services/fileService";

const UserServiceInfoTab = ({ userService, reloadDetailUserService, lockParent }) => {
  const fileUploadModal = useRef(null);

  const [title, setTitle] = useState(userService.title);
  const [description, setDescription] = useState(userService.description);
  const [isForClients, setIsForClients] = useState();
  const [isForHorses, setIsForHorses] = useState();
  const [defaultImage, setDefaultImage] = useState(userService.defaultImage);
  const [defaultImageId, setDefaultImageId] = useState(userService.defaultImageId);
  const [tags, setTags] = useState(userService.tags);
  const [isModified, setIsModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const descEditorRef = useRef(null);
  const [editorResetKey, setEditorResetKey] = useState(0);
  const { updateUserService } = useUserServices();

  useEffect(() => {
    setTitle(userService.title);
    setIsForClients(userService.isForClients);
    setIsForHorses(userService.isForHorses);
    setDescription(userService.description);
    setDefaultImageId(userService.defaultImageId);
    setTags(userService.tags);
  }, [userService]);

  const initialData = {
    title: userService.title,
    description: userService.description,
    isForClients: userService.isForClients,
    isForHorses: userService.isForHorses,
    tags: userService.tags,
    defaultImageId: userService.defaultImageId,
    defaultImage: userService.defaultImage,
  };

  const handleChange = (setter) => (event) => {
    setter(event.target.value);
    setIsModified(true);
    lockParent(true);
  };

  const handleSave = async () => {
    const isValid = validate();
    if (!isValid) return;
    setIsSaving(true);
    const updatedUserServiceInfo = {
      externalId: userService.externalId,
      title,
      isForClients,
      isForHorses,
      description,
      tags,
      defaultImageId,
    };
    const result = await UserServiceService.Update(updatedUserServiceInfo);
    if (!result) {
      setIsSaving(false);
      return false;
    }

    reloadDetailUserService(userService);
    updateUserService(userService.externalId);
    setIsModified(false);
    lockParent(false);
    setIsSaving(false);
  };

  const validate = () => {
    if (title === "") {
      addToast(
        i18n.t("UserServiceForm.validation.NameRequired"),
        "userServiceFormValidation",
        "ERROR",
      );
      return false;
    }

    if (description.length > 40000) {
      addToast(
        i18n.t("ClientForm.Validation.InvalidDescriptionLen"),
        "clientFormValidationEmail",
        "ERROR",
      );
      return false;
    }

    return true;
  };

  const handleReset = () => {
    setDefaultImage(initialData.defaultImage);
    setDefaultImageId(initialData.defaultImageId);
    setTitle(initialData.title);
    setIsForClients(initialData.isForClients);
    setIsForHorses(initialData.isForHorses);

    const initialHtml = userService.description || "";
    setDescription(initialHtml);
    descEditorRef.current?.setHTML(initialHtml);
    setEditorResetKey((k) => k + 1);

    setTags(initialData.tags);
    setIsModified(false);
    lockParent(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.leftSection}>
          <div className={styles.row}>
            <label className={styles.label}>{i18n.t("Generic.Name")}:</label>
            <input
              type="text"
              className={styles.input}
              value={title}
              maxLength={200}
              onChange={handleChange(setTitle)}
            />
          </div>

          <div className={styles.row}>
            <label className={styles.label}>
              {i18n.t("UserServiceQuickForm.FieldAvailability")}
            </label>
            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isForClients}
                  onChange={(e) => {
                    setIsForClients(e.target.checked);
                    setIsModified(true);
                    lockParent(true);
                  }}
                />
                {i18n.t("UserServiceQuickForm.FieldAvailability.Clients")}
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isForHorses}
                  onChange={(e) => {
                    setIsForHorses(e.target.checked);
                    setIsModified(true);
                    lockParent(true);
                  }}
                />
                {i18n.t("UserServiceQuickForm.FieldAvailability.Horses")}
              </label>
            </div>
          </div>

          <div className={styles.row}>
            <label className={styles.label}>{i18n.t("Generic.Description")}:</label>

            <RichEditor
              ref={descEditorRef}
              key={`client-desc-${userService.externalId}-${editorResetKey}`}
              controlled
              value={description}
              onChange={(html) => {
                setDescription(html);

                const left = RichEditorHelper.normalizeHtmlForCompare(html);
                const right = RichEditorHelper.normalizeHtmlForCompare(userService.description);

                if (left !== right) {
                  setIsModified(true);
                  lockParent(true);
                }
              }}
              placeholder={""}
              height={300}
              showEmoji
              showTextColor
              showFontSize
              showBold
              showItalic
              showUnderline
              showStrikethrough
            />
          </div>
        </div>

        <div className={styles.rightSection}>
          <div className={styles.metaInfo}>
            <label className={styles.label}>
              {i18n.t("ClientForm.CreationDate")}:{" "}
              {DateTimeService.formatDateString(userService.createdOn, true)}
            </label>
            {userService.modifiedOn && (
              <label className={styles.label}>
                {i18n.t("ClientForm.UpdateDate")}:{" "}
                {DateTimeService.formatDateString(userService.modifiedOn, true)}
              </label>
            )}
          </div>

          <label className={styles.label}>{i18n.t("Generic.DefaultImage")}:</label>
          <div className={styles.imgPreview}>
            <ImagePreview imageUrl={defaultImage} />
            <FileUploaderModal
              showMultiImageTab={false}
              ref={fileUploadModal}
              callback={async (thumbnail, fileId) => {
                const img = await FileService.getFile(fileId);
                setDefaultImage(img);
                setDefaultImageId(fileId);
                setIsModified(true);
                lockParent(true);
                fileUploadModal.current.close();
              }}
              allowMultipleFiles={false}
            />
            <div className={styles.buttonUpdateDefaultPictureContainer}>
              <button
                className={styles.buttonUpdateDefaultPicture}
                onClick={() => fileUploadModal.current.open()}>
                {i18n.t("PictureUploadField.LoadAnImage")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <button className={styles.formSaveButton} disabled={!isModified} onClick={handleSave}>
          {i18n.t("Generic.Save")}
        </button>
        <button className={styles.formResetButton} disabled={!isModified} onClick={handleReset}>
          {i18n.t("Generic.ResetForm")}
        </button>
      </div>

      {isSaving && (
        <div className={styles.savingOverlay}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default UserServiceInfoTab;
